import { Box, CircularProgress, Container, Fab, Fade, Paper, Stack, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useCallback, useContext, useState, useEffect } from 'react';
import { ViewOnlyContext, useEventDetails } from '../context';
import { WasteDataContext, IWasteDataContext } from '../context';
import DataViewTable from './DataViewTable';
import EditDataButton from './EditDataButton';
import DataInputForm from './DataInputForm';
import FloatingPagination from './FloatingPagination';
import { EDIT_DATA_BUTTON_HEIGHT } from 'src/components/EditDataButton';
import NoEventError from './NoEventError';
import { WasteMeasurementWithMaterial } from 'src/schema';
import ReportsScreenLink from './ReportsScreenLink';
import WasteDataExportButton from './WasteDataExportButton';
import DataSummary from './DataSummary';
import { usePermissions } from 'src/hooks/usePermissions';
import { EVENT_BOTTOM_NAV_HEIGHT, EVENT_TAB_SCROLL_END_GAP_PX } from 'src/spacing';

interface Props {
    eventId: string;
}

export default function EventData(props: Props) {
    const [selectedRow, setSelectedRow] = useState<WasteMeasurementWithMaterial | null>(null);
    const viewOnly = useContext(ViewOnlyContext);
    const { canEditWasteData } = usePermissions();
    const [editMode, setEditMode] = useState(false);
    const [todoMode, setTodoMode] = useState(false);
    const toggleEditMode = useCallback(() => {
        setEditMode(!viewOnly && !editMode && canEditWasteData);
        setSelectedRow(null);
    }, [editMode, viewOnly, canEditWasteData, setEditMode, setSelectedRow]);
    const toggleTodoMode = useCallback(() => {
        setTodoMode(!viewOnly && !todoMode);
        setSelectedRow(null);
    }, [todoMode, viewOnly, setTodoMode, setSelectedRow]);

    const [datasetIndex, setDatasetIndex] = useState(0);
    const [showBackToTop, setShowBackToTop] = useState(false);

    useEffect(() => {
        const container = document.getElementById('event-content');
        const handleScroll = () => {
            setShowBackToTop((container?.scrollTop || 0) > 500);
        };
        container?.addEventListener('scroll', handleScroll);
        return () => container?.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        document.getElementById('event-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const { event, isLoading, error } = useEventDetails(props.eventId);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading event details: {error.message}</Typography>;
    }

    if (!event) {
        return <NoEventError eventId={props.eventId} />;
    }

    const datasets = event?.datasets || [];
    // Clamp datasetIndex when datasets shrink (e.g. a dataset was deleted)
    const safeDatasetIndex = datasetIndex >= datasets.length ? Math.max(0, datasets.length - 1) : datasetIndex;
    const dataset = datasets[safeDatasetIndex];
    const datasetId = dataset?.id || '';

    const dataContext: IWasteDataContext = {
        datasetId,
        editMode,
        todoMode,
        toggleEditMode,
        toggleTodoMode,
        selectedRow,
        setSelectedRow,
    };

    return (
        <WasteDataContext.Provider value={dataContext}>
            <Box id="event-data" sx={{ width: '100%', height: '100%' }}>
                <Container
                    maxWidth="md"
                    id="data-view-table-container"
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingBottom: `${EVENT_TAB_SCROLL_END_GAP_PX}px`,
                    }}
                >
                    {dataset?.name ? (
                        <Typography
                            variant="h5"
                            sx={{
                                mt: 2,
                                mb: 1.5,
                                fontWeight: 500,
                                alignSelf: 'flex-start',
                            }}
                        >
                            Dataset: {dataset.name}
                        </Typography>
                    ) : null}
                    <Stack
                        direction="row"
                        sx={{
                            width: '100%',
                            justifyContent: 'space-around',
                            mb: 2,
                        }}
                    >
                        <ReportsScreenLink eventId={props.eventId} />
                        <WasteDataExportButton eventId={props.eventId} />
                        {!viewOnly && <EditDataButton />}
                    </Stack>
                    <Paper
                        id="data-view-table-paper"
                        sx={{
                            width: '100%',
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                        }}
                    >
                        <DataSummary datasetId={datasetId} eventId={props.eventId} />
                        <DataViewTable datasetId={datasetId} />
                    </Paper>
                    <Box sx={{ height: EDIT_DATA_BUTTON_HEIGHT + 8 }} />
                    {!editMode && Array.isArray(datasets) && datasets.length > 1 && (
                        <FloatingPagination
                            numPages={datasets.length}
                            currentPage={safeDatasetIndex}
                            setPage={setDatasetIndex}
                            sx={{ bottom: `${EVENT_BOTTOM_NAV_HEIGHT}px` }}
                        />
                    )}
                </Container>
                {editMode && <DataInputForm eventId={props.eventId} />}
                <Fade in={showBackToTop}>
                    <Fab
                        variant="extended"
                        size="medium"
                        color="primary"
                        onClick={scrollToTop}
                        sx={{
                            position: 'fixed',
                            top: 60,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1000,
                        }}
                        aria-label="back to top"
                    >
                        Back to top <ArrowUpwardIcon sx={{ ml: 0.5 }} />
                    </Fab>
                </Fade>
            </Box>
        </WasteDataContext.Provider>
    );
}
